import { ARTICLES, ArticleLink } from 'components/intercom';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { useFeatures } from 'utils/util/features';
import { OBJECTIVES } from '../../consts/consts';
import { Me, Organization, OrgChartRelatives, Person } from '../../types';
import { getDatePart } from '../../utils/util/time';
import usePersonOrgChartRelatives from '../Person/PersonOrgChartRelatives';
import Loading from '../Widgets/Loading';
import ObjectivesHoverablePersonCard from './ObjectivesHoverablePersonCard';
import { DATE_PARAMETER } from './ObjectivesTimeFrameSelector';
import StrategicObjectivesPanel from './StrategicObjectivesPanel';

interface Props {
  currentOrganization: Organization;
  currentProxyPerson: Person;
  me: Me;
  person: Person;
  currentFirstDayOfTimeFrame: Date;
  firstDay: string;
  lastDay: string;
  isDemoOrPreviewMode?: boolean;
  demoOrgChartRelatives?: OrgChartRelatives;
}

const ObjectivesTeamNav: FC<Props> = ({
  currentOrganization,
  currentProxyPerson,
  me,
  person,
  currentFirstDayOfTimeFrame,
  firstDay,
  lastDay,
  isDemoOrPreviewMode = false,
  demoOrgChartRelatives,
}) => {
  const { isGroupAndOptionEnabled } = useFeatures();
  const strategicObjectivesInSidebarEnabled = isGroupAndOptionEnabled(
    'objectives',
    'objective_types_enabled'
  );
  const { formatMessage } = useIntl();
  // Skip API call in preview mode by passing enabled=false to the hook
  const fetchedOrgChartRelatives: OrgChartRelatives | undefined =
    usePersonOrgChartRelatives(
      currentOrganization,
      currentProxyPerson,
      person,
      !isDemoOrPreviewMode
    );
  const orgChartRelatives = isDemoOrPreviewMode
    ? (demoOrgChartRelatives ?? {
        manager: null,
        additional_managers: [],
        above_manager: [],
        direct_reports: [],
        peers: [],
      })
    : fetchedOrgChartRelatives;
  const urlSuffix = `${
    OBJECTIVES(formatMessage).path
  }?${DATE_PARAMETER}=${getDatePart(currentFirstDayOfTimeFrame)}`;

  if (orgChartRelatives === undefined) {
    return <Loading />;
  }

  const { manager, additional_managers, above_manager, peers, direct_reports } =
    orgChartRelatives;

  return (
    <>
      <Col className="col-12 col-md-3 mt-4 mt-md-0">
        {strategicObjectivesInSidebarEnabled && (
          <StrategicObjectivesPanel
            currentOrganization={currentOrganization}
            me={me}
            firstDay={firstDay}
            lastDay={lastDay}
            isDemoOrPreviewMode={isDemoOrPreviewMode}
            header={
              <h3>
                <FormattedMessage
                  id="app.views.objectives.team_navigation.header.strategic"
                  defaultMessage="View strategic objectives"
                />
              </h3>
            }
          />
        )}
        {(manager ||
          additional_managers.length > 0 ||
          peers.length > 0 ||
          direct_reports.length > 0) && (
          <>
            <h3>
              <FormattedMessage
                id="app.views.objectives.team_navigation.header.text"
                defaultMessage="View team objectives"
              />
            </h3>
            {!strategicObjectivesInSidebarEnabled && (
              <StrategicObjectivesPanel
                currentOrganization={currentOrganization}
                me={me}
                firstDay={firstDay}
                lastDay={lastDay}
                isDemoOrPreviewMode={isDemoOrPreviewMode}
              />
            )}
            {manager && (
              <>
                <h6 className="header-pretitle mt-4">
                  <FormattedMessage
                    id="app.views.person.person_objectives.team_navigation.manager_and_above.text"
                    defaultMessage="Manager and above"
                  />
                </h6>
                {above_manager.length > 0 && (
                  <>
                    {above_manager.map((p) => (
                      <ObjectivesHoverablePersonCard
                        key={p.id}
                        person={p}
                        urlSuffix={urlSuffix}
                        isDemoOrPreviewMode={isDemoOrPreviewMode}
                      />
                    ))}
                  </>
                )}
                <ObjectivesHoverablePersonCard
                  person={manager}
                  urlSuffix={urlSuffix}
                  isDemoOrPreviewMode={isDemoOrPreviewMode}
                />
              </>
            )}
            {additional_managers.length > 0 && (
              <>
                <h6 className="header-pretitle mt-4">
                  <FormattedMessage
                    id="app.views.person.person_objectives.team_navigation.additional_managers.text"
                    defaultMessage="{count, plural, one {Additional manager} other {Additional managers}}"
                    values={{ count: additional_managers.length }}
                  />
                </h6>
                {additional_managers.map((p) => (
                  <ObjectivesHoverablePersonCard
                    key={p.id}
                    person={p}
                    urlSuffix={urlSuffix}
                    isDemoOrPreviewMode={isDemoOrPreviewMode}
                  />
                ))}
              </>
            )}
            {direct_reports.length > 0 && (
              <>
                <h6 className="header-pretitle mt-4">
                  <FormattedMessage
                    id="app.views.person.person_objectives.team_navigation.direct_reports.text"
                    defaultMessage="Direct reports"
                  />
                </h6>
                {direct_reports.map((p) => (
                  <ObjectivesHoverablePersonCard
                    key={p.id}
                    person={p}
                    urlSuffix={urlSuffix}
                    isDemoOrPreviewMode={isDemoOrPreviewMode}
                  />
                ))}
              </>
            )}
            {peers.length > 0 && (
              <>
                <h6 className="header-pretitle mt-4">
                  <FormattedMessage
                    id="app.views.person.person_objectives.team_navigation.peers.text"
                    defaultMessage="Peers"
                  />
                </h6>
                {peers.map((p) => (
                  <ObjectivesHoverablePersonCard
                    key={p.id}
                    person={p}
                    urlSuffix={urlSuffix}
                    isDemoOrPreviewMode={isDemoOrPreviewMode}
                  />
                ))}
              </>
            )}
          </>
        )}
        <Row className="pt-3">
          <Col className="col-12">
            <ArticleLink article={ARTICLES.OKRS}>
              <FormattedMessage
                id="app.views.person.person_objectives.nav_link.learn_more"
                defaultMessage="Learn more about OKRs"
              />{' '}
              <i className="fe fe-external-link" />
            </ArticleLink>
          </Col>
        </Row>
      </Col>
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentOrganization, currentProxyPerson, me } = state;

  return {
    currentOrganization,
    currentProxyPerson,
    me,
  };
};

export default connect(mapStateToProps)(ObjectivesTeamNav);
